import { useMutation } from '@apollo/client';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';
import { Button } from '../../components/Button/Button';
import { useModal } from '../../components/Modal/PresentedModal';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { history } from '../../model/history';
import { handleError } from '../../model/notification';
import { newsDetailsPath } from '../../routes';
import { NewsForm, NewsFormFields } from './news_form';
import { parseJsonString } from '../../util/data';

const CREATE_NEWS = graphql(`
  mutation CreateNews(
    $shopId: ID!
    $titleDe: String
    $titleEn: String
    $textDe: String
    $textEn: String
    $category: String
    $url: String
    $inverse: Boolean
    $order: Int
    $asset: Upload
    $meta: JSON
  ) {
    createNews(
      input: {
        shopId: $shopId
        titleDe: $titleDe
        titleEn: $titleEn
        textDe: $textDe
        textEn: $textEn
        category: $category
        url: $url
        inverse: $inverse
        order: $order
        asset: $asset
        meta: $meta
      }
    ) {
      news {
        id
      }
      clientMutationId
    }
  }
`);

interface Props {
  shopId?: string;
}

export function CreateNewsItem({ shopId }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const [create] = useMutation(CREATE_NEWS);

  const { fields, valid, dirty, onSubmit, submissionStatus } = useForm<NewsFormFields>({
    model: {
      shopId,
    },
    validations: {
      shopId: 'required',
      meta: 'json',
    },
    onSubmit: async ({ changes, model }) => {
      if (model.shopId) {
        const response = await create({
          variables: {
            ...model,
            shopId: model.shopId,
            ...('meta' in changes ? { meta: parseJsonString(changes.meta) } : {}),
          },
        });
        const id = response.data?.createNews?.news?.id;

        if (id) {
          history.push(newsDetailsPath(id));
        }

        close();
      }
    },
    onSubmitError: (error) => handleError(error),
  });

  return (
    <Modal
      open={open}
      title={t.actions.create(t.model.news._name)}
      footer={
        <>
          <Button
            title={t.actions.cancel('')}
            onClick={() => {
              close();
            }}
          />
          <Button
            type="primary"
            title={t.actions.save('')}
            onClick={onSubmit}
            disabled={!dirty && !valid}
            loading={submissionStatus === 'submitting'}
          />
        </>
      }
    >
      <NewsForm fields={fields} />
    </Modal>
  );
}
